import type { AiContext } from '@/services/ai.service';
import * as aiService from '@/services/ai.service';
import { create } from 'zustand';

export type AiProposalKind = 'contract' | 'patch' | 'test' | 'suggestion' | 'dapp';
export type AiProposalAction = 'replace' | 'append' | 'new-file' | 'scaffold';
export type AiProposalStageStatus = 'success' | 'error';

/** A before-snapshot taken when a proposal is applied, enabling undo */
export interface CodeSnapshot {
  id: string;
  filePath: string;
  content: string;
  timestamp: number;
  label: string; // e.g. "Before: Optimized Code"
}

export interface AiProposal {
  code: string;
  language: string;
  title: string;
  kind: AiProposalKind;
  preferredAction: AiProposalAction;
  actionLabel?: string;
  targetPath?: string;
  runCompileAfterApply?: boolean;
  runTestsAfterApply?: boolean;
}

export interface AiProposalStatus {
  applied?: AiProposalStageStatus;
  compile?: AiProposalStageStatus;
  tests?: AiProposalStageStatus;
}

export interface AiMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  task?: string;
  timestamp: number;
  proposal?: AiProposal;
  proposalStatus?: AiProposalStatus;
}

interface AiAssistantState {
  messages: AiMessage[];
  isLoading: boolean;
  error: string | null;
  /** History of code snapshots taken before applying AI proposals — enables undo */
  codeHistory: CodeSnapshot[];

  appendMessage: (message: Omit<AiMessage, 'id' | 'timestamp'>) => void;
  updateProposalStatus: (messageId: string, patch: Partial<AiProposalStatus>) => void;
  sendMessage: (content: string, task?: string, context?: Record<string, unknown>) => Promise<void>;
  fixCompileError: (
    code: string,
    errors: string[],
    context?: Record<string, unknown>,
  ) => Promise<void>;
  /**
   * Call this BEFORE applying an AI proposal to the editor.
   * Keeps the last 20 snapshots so users can restore previous versions.
   */
  snapshotCode: (filePath: string, content: string, label: string) => void;
  clearMessages: () => void;
  clearError: () => void;
}

let messageIdCounter = 0;
function nextId(): string {
  return `msg_${Date.now()}_${++messageIdCounter}`;
}

/**
 * Build AiContext from the raw context object passed by UI components
 */
function toAiContext(ctx: Record<string, unknown> = {}): AiContext {
  const files: Record<string, string> = {};
  if (ctx.code && ctx.fileName) {
    files[ctx.fileName as string] = ctx.code as string;
  }
  return {
    currentFile: ctx.fileName as string | undefined,
    currentCode: ctx.code as string | undefined,
    files,
  };
}

function buildProposal(
  code: string | undefined,
  task: string,
  options: Partial<
    Pick<
      AiProposal,
      | 'title'
      | 'actionLabel'
      | 'language'
      | 'targetPath'
      | 'kind'
      | 'preferredAction'
      | 'runCompileAfterApply'
      | 'runTestsAfterApply'
    >
  > = {},
): AiProposal | undefined {
  if (!code) return undefined;

  const titleByTask: Record<string, string> = {
    contract_template: 'Generated Contract',
    gas_optimize: 'Optimized Code',
    test_generate: 'Generated Tests',
    fix_error: 'Compile Fix',
    chat: 'AI Code Suggestion',
  };

  const kindByTask: Record<string, AiProposalKind> = {
    contract_template: 'contract',
    gas_optimize: 'patch',
    test_generate: 'test',
    fix_error: 'patch',
    chat: 'suggestion',
  };

  const preferredActionByTask: Record<string, AiProposalAction> = {
    contract_template: 'new-file',
    gas_optimize: 'replace',
    test_generate: 'new-file',
    fix_error: 'replace',
    chat: 'replace',
  };

  return {
    code,
    language: options.language ?? 'solidity',
    title: options.title ?? titleByTask[task] ?? 'AI Proposal',
    kind: options.kind ?? kindByTask[task] ?? 'suggestion',
    preferredAction: options.preferredAction ?? preferredActionByTask[task] ?? 'replace',
    actionLabel: options.actionLabel,
    targetPath: options.targetPath,
    runCompileAfterApply: options.runCompileAfterApply,
    runTestsAfterApply: options.runTestsAfterApply,
  };
}

function inferTestPath(currentFile: string | undefined): string | undefined {
  if (!currentFile) return undefined;

  const fileName = currentFile.split('/').pop();
  if (!fileName) return undefined;

  if (fileName.endsWith('.sol')) {
    const base = fileName.replace(/\.sol$/, '');
    return `test/${base}.t.sol`;
  }

  return `test/${fileName}.test.sol`;
}

export const useAiAssistantStore = create<AiAssistantState>((set) => ({
  messages: [
    {
      id: 'system_welcome',
      role: 'system',
      content:
        'AI Assistant ready. Generate contracts, audit security, optimize gas, fix errors, or generate tests — all context-aware.',
      timestamp: Date.now(),
    },
  ],
  isLoading: false,
  error: null,
  codeHistory: [],

  snapshotCode: (filePath, content, label) => {
    set((s) => {
      const snapshot: CodeSnapshot = {
        id: `snap_${Date.now()}`,
        filePath,
        content,
        timestamp: Date.now(),
        label,
      };
      // Keep latest 20 snapshots
      const history = [snapshot, ...s.codeHistory].slice(0, 20);
      return { codeHistory: history };
    });
  },

  appendMessage: (message) => {
    set((s) => ({
      messages: [
        ...s.messages,
        {
          ...message,
          id: nextId(),
          timestamp: Date.now(),
        },
      ],
    }));
  },

  updateProposalStatus: (messageId, patch) => {
    set((s) => ({
      messages: s.messages.map((message) =>
        message.id === messageId
          ? {
              ...message,
              proposalStatus: {
                ...message.proposalStatus,
                ...patch,
              },
            }
          : message,
      ),
    }));
  },

  sendMessage: async (content, task = 'chat', context = {}) => {
    const userMsg: AiMessage = {
      id: nextId(),
      role: 'user',
      content,
      task,
      timestamp: Date.now(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg],
      isLoading: true,
      error: null,
    }));

    try {
      const ctx = toAiContext(context);
      let responseText = '';
      let proposal: AiProposal | undefined;

      switch (task) {
        case 'contract_template': {
          const res = await aiService.generate(content, ctx);
          responseText = res.explanation
            ? `${res.explanation}\n\n\`\`\`solidity\n${res.code}\n\`\`\``
            : `\`\`\`solidity\n${res.code}\n\`\`\``;
          proposal = buildProposal(res.code, task, { title: 'Generated Contract' });
          break;
        }

        case 'security_audit': {
          const code = ctx.currentCode ?? content;
          const res = await aiService.audit(code, ctx);
          responseText = `## Security Audit — Risk: ${res.riskLevel?.toUpperCase() ?? 'UNKNOWN'}\n\n${res.summary}\n\n`;
          if (res.findings?.length) {
            responseText += res.findings
              .map(
                (f, i) =>
                  `### ${i + 1}. [${f.severity?.toUpperCase()}] ${f.title}\n${f.description}${f.recommendation ? '\n\n**Fix:** ' + f.recommendation : ''}`,
              )
              .join('\n\n');
          }
          break;
        }

        case 'gas_optimize': {
          const code = ctx.currentCode ?? content;
          const res = await aiService.optimize(code, ctx);
          responseText = `## Gas Optimization\n\n**Estimated savings:** ${res.estimatedSavings ?? 'varies'}\n\n`;
          if (res.suggestions?.length) {
            responseText += res.suggestions
              .map(
                (s, i) => `### ${i + 1}. ${s.title}\n${s.description}\n- Gas saved: ${s.gasSaved}`,
              )
              .join('\n\n');
          }
          if (res.code) {
            responseText += `\n\n### Optimized Code\n\n\`\`\`solidity\n${res.code}\n\`\`\``;
            proposal = buildProposal(res.code, task, {
              title: 'Optimized Code',
              targetPath: ctx.currentFile,
              kind: 'patch',
              preferredAction: 'replace',
              runTestsAfterApply: true,
            });
          }
          break;
        }

        case 'code_explain': {
          const code = ctx.currentCode ?? content;
          const res = await aiService.explain(code, ctx);
          responseText = res.explanation;
          break;
        }

        case 'test_generate': {
          const code = ctx.currentCode ?? content;
          const res = await aiService.generateTests(code, ctx);
          responseText = `## Generated Tests (${res.framework ?? 'unknown framework'})\n\n${res.explanation ?? ''}\n\n\`\`\`solidity\n${res.code}\n\`\`\``;
          proposal = buildProposal(res.code, task, {
            title: 'Generated Tests',
            actionLabel: 'Create Test File',
            targetPath: inferTestPath(ctx.currentFile),
            kind: 'test',
            preferredAction: 'new-file',
            runTestsAfterApply: true,
          });
          break;
        }

        case 'fix_error': {
          const code = ctx.currentCode ?? '';
          const res = await aiService.fix(code, content, ctx);
          responseText = `## Fix Applied\n\n${res.explanation}\n\n`;
          if (res.changes?.length) {
            responseText += `**Changes:**\n${res.changes.map((c) => `- ${c}`).join('\n')}\n\n`;
          }
          responseText += `\`\`\`solidity\n${res.code}\n\`\`\``;
          proposal = buildProposal(res.code, task, {
            title: 'Compile Fix',
            actionLabel: 'Apply Fix',
            targetPath: ctx.currentFile,
            kind: 'patch',
            preferredAction: 'replace',
            runCompileAfterApply: true,
            runTestsAfterApply: true,
          });
          break;
        }

        default: {
          // General chat — prefer streaming vibe endpoint; fall back to explain/generate
          if (ctx.currentCode) {
            // Add a placeholder assistant message that gets appended to in real time
            const streamMsg: AiMessage = {
              id: nextId(),
              role: 'assistant',
              content: '',
              task,
              timestamp: Date.now(),
            };
            set((s) => ({ messages: [...s.messages, streamMsg] }));

            try {
              const { text } = await aiService.vibeStream(
                ctx.currentCode + '\n\nQuestion: ' + content,
                ctx,
                (token) => {
                  set((s) => ({
                    messages: s.messages.map((m) =>
                      m.id === streamMsg.id ? { ...m, content: m.content + token } : m,
                    ),
                  }));
                },
              );
              responseText = text;
              // The message has already been streamed directly into state; skip the
              // normal "push assistantMsg" below by setting responseText but
              // returning early from the else path.
              set((s) => ({
                messages: s.messages.map((m) =>
                  m.id === streamMsg.id ? { ...m, content: responseText } : m,
                ),
                isLoading: false,
              }));
              return; // early return — message already in state
            } catch {
              // Fall back to batch explain
              const res = await aiService.explain(
                ctx.currentCode + '\n\nQuestion: ' + content,
                ctx,
              );
              responseText = res.explanation;
              // Remove the placeholder and let normal push happen
              set((s) => ({ messages: s.messages.filter((m) => m.id !== streamMsg.id) }));
            }
          } else {
            // No open file — use generate or vibe stream
            try {
              const streamMsg: AiMessage = {
                id: nextId(),
                role: 'assistant',
                content: '',
                task,
                timestamp: Date.now(),
              };
              set((s) => ({ messages: [...s.messages, streamMsg] }));

              const { text } = await aiService.vibeStream(content, ctx, (token) => {
                set((s) => ({
                  messages: s.messages.map((m) =>
                    m.id === streamMsg.id ? { ...m, content: m.content + token } : m,
                  ),
                }));
              });

              set((s) => ({
                messages: s.messages.map((m) =>
                  m.id === streamMsg.id ? { ...m, content: text } : m,
                ),
                isLoading: false,
              }));
              return; // early return
            } catch {
              const res = await aiService.generate(content, ctx);
              responseText = res.explanation
                ? `${res.explanation}\n\n\`\`\`solidity\n${res.code}\n\`\`\``
                : res.code;
              proposal = buildProposal(res.code, task, {
                title: 'AI Code Suggestion',
                targetPath: ctx.currentFile,
                kind: 'suggestion',
                preferredAction: ctx.currentFile ? 'replace' : 'new-file',
              });
            }
          }
          break;
        }
      }

      const assistantMsg: AiMessage = {
        id: nextId(),
        role: 'assistant',
        content: responseText,
        task,
        timestamp: Date.now(),
        proposal,
      };

      set((s) => ({
        messages: [...s.messages, assistantMsg],
        isLoading: false,
      }));
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'AI request failed';
      set({ error: errorMsg, isLoading: false });
    }
  },

  /**
   * Special method for the compile-error → AI fix loop.
   * Publishes the fix as an assistant proposal for explicit review/apply.
   */
  fixCompileError: async (code, errors, context = {}) => {
    const errorText = errors.join('\n');

    const userMsg: AiMessage = {
      id: nextId(),
      role: 'user',
      content: `🔧 Fix compile errors:\n\`\`\`\n${errorText}\n\`\`\``,
      task: 'fix_error',
      timestamp: Date.now(),
    };

    set((s) => ({
      messages: [...s.messages, userMsg],
      isLoading: true,
      error: null,
    }));

    try {
      const ctx = toAiContext(context);
      const res = await aiService.fix(code, errorText, ctx);

      const assistantMsg: AiMessage = {
        id: nextId(),
        role: 'assistant',
        content: `## Fix Applied\n\n${res.explanation}\n\n${res.changes?.length ? `**Changes:**\n${res.changes.map((c) => `- ${c}`).join('\n')}\n\n` : ''}\`\`\`solidity\n${res.code}\n\`\`\``,
        task: 'fix_error',
        timestamp: Date.now(),
        proposal: buildProposal(res.code, 'fix_error', {
          title: 'Compile Fix',
          actionLabel: 'Apply Fix',
          targetPath: ctx.currentFile,
          kind: 'patch',
          preferredAction: 'replace',
          runCompileAfterApply: true,
          runTestsAfterApply: true,
        }),
      };

      set((s) => ({
        messages: [...s.messages, assistantMsg],
        isLoading: false,
      }));
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'AI fix failed';
      set({ error: errorMsg, isLoading: false });
    }
  },

  clearMessages: () =>
    set({
      messages: [
        {
          id: 'system_welcome',
          role: 'system',
          content:
            'AI Assistant ready. Generate contracts, audit security, optimize gas, fix errors, or generate tests — all context-aware.',
          timestamp: Date.now(),
        },
      ],
      error: null,
    }),

  clearError: () => set({ error: null }),
}));
